import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import homeIcon from '../../assets/icons/home.png';
import rulerIcon from '../../assets/icons/ruler-icon.png';
import weatherIcon from '../../assets/icons/weather-icon.png';
import plusIcon from  '../../assets/icons/plus-icon-black.png';
import globeIcon from '../../assets/icons/globe-icon.png';
import { useUserContext } from '../../contexts/UserContext';
import { Tooltip } from '@mui/material';
import mapboxgl from 'mapbox-gl';
import WeatherConditions from '../WeatherConditions';

export default function MapWidgets(props) {
    const {
        is3D,
        setIs3D
    } = useUserContext();

    const {
        map,
        setPanel,
        panel
    } = props;

    const [rulerActive, setRulerActive] = useState(false);
    const markersRef    = useRef([]);
    const rulerPopupRef = useRef(null);

    const [weatherActive, setWeatherActive] = useState(false);
    const weatherMarkerRef = useRef(null);
    const weatherPopupRef  = useRef(null);
  
    const calculateRulerDistance = (c1, c2) => {
      const R = 6371, f = 0.621371;
      const [lon1, lat1] = c1, [lon2, lat2] = c2;
      const dLat = (lat2 - lat1) * Math.PI/180;
      const dLon = (lon2 - lon1) * Math.PI/180;
      const a =
        Math.sin(dLat/2)**2 +
        Math.cos(lat1*Math.PI/180) *
        Math.cos(lat2*Math.PI/180) *
        Math.sin(dLon/2)**2;
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
      return R * c * f;
    };

    useEffect(() => {
        if (!map?.current) return;

        // RULER CLICK HANDLER
        const handleRulerClick = (e) => {
        if (markersRef.current.length >= 2) return;
        const loc = [e.lngLat.lng, e.lngLat.lat];
        const el = document.createElement('div');
        el.className = 'map-ruler-coord';
        const m = new mapboxgl.Marker(el)
            .setLngLat(loc)
            .addTo(map.current);
        markersRef.current.push(m);

        if (markersRef.current.length === 2) {
            const coords = markersRef.current.map(m => m.getLngLat().toArray());
            const dist = calculateRulerDistance(coords[0], coords[1]);
            const data = { type: 'FeatureCollection', features: [{ type: 'Feature', geometry: { type: 'LineString', coordinates: coords } }] };

            if (!map.current.getSource('RulerSource')) {
            map.current.addSource('RulerSource', { type: 'geojson', data });
            map.current.addLayer({ id: 'RulerLayer', type: 'line', source: 'RulerSource', paint: { 'line-color': 'red', 'line-width': 2 } });
            } else {
            map.current.getSource('RulerSource').setData(data);
            }

            if (rulerPopupRef.current) rulerPopupRef.current.remove();
            rulerPopupRef.current = new mapboxgl.Popup({ offset: 25, maxWidth: '300px', className: 'popup' })
            .setLngLat(coords[1])
            .setHTML(`
                <div class='alert-popup-title'>Distance Measured</div>
                <div class='alert-popup-row'>
                <div class='alert-popup-row-name'>Miles:</div>
                <div class='alert-popup-row-value'>${dist.toFixed(2)}</div>
                </div>
                <div class='alert-popup-row'>
                <div class='alert-popup-row-name'>Kilometers:</div>
                <div class='alert-popup-row-value'>${(dist*1.60934).toFixed(2)}</div>
                </div>
                <div class="alert-popup-row-button js-clear-ruler">Clear</div>
            `)
            .addTo(map.current);

            const popupEl = rulerPopupRef.current.getElement();
            const clearBtn = popupEl.querySelector('.js-clear-ruler');
            clearBtn.addEventListener('click', () => {
            // remove the line layer & source
            if (map.current.getLayer('RulerLayer')) {
                map.current.removeLayer('RulerLayer');
            }
            if (map.current.getSource('RulerSource')) {
                map.current.removeSource('RulerSource');
            }
            // remove the two markers
            markersRef.current.forEach(m => m.remove());
            markersRef.current = [];
            // close and drop the popup
            rulerPopupRef.current.remove();
            rulerPopupRef.current = null;
            });
        }
        };

        // WEATHER CLICK HANDLER
        const handleWeatherClick = (e) => {
        const loc = [e.lngLat.lng, e.lngLat.lat];

        // cleanup old
        if (weatherMarkerRef.current) weatherMarkerRef.current.remove();
        if (weatherPopupRef.current) weatherPopupRef.current.remove();

        // marker ripple
        const point = document.createElement('div');
        point.className = 'map-weather-point';
        const rippleContainer = document.createElement('div'); rippleContainer.className = 'ripple-container';
        const centerImg = document.createElement('div'); centerImg.className = 'center-image';
        rippleContainer.appendChild(centerImg);
        for (let i=0; i<3; i++) {
            const r = document.createElement('div'); r.className='ripple'; rippleContainer.appendChild(r);
        }
        point.appendChild(rippleContainer);
        const marker = new mapboxgl.Marker(point).setLngLat(loc).addTo(map.current);
        weatherMarkerRef.current = marker;

        const popupNode = document.createElement('div');
        ReactDOM.render(<WeatherConditions coordinates={[loc[1], loc[0]]} />, popupNode);

        weatherPopupRef.current = new mapboxgl.Popup({ offset:25, maxWidth:'400px', className:'popup' })
            .setLngLat(loc)
            .setDOMContent(popupNode)
            .addTo(map.current);
        };

        if (rulerActive)  map.current.on('click', handleRulerClick);
        if (weatherActive) map.current.on('click', handleWeatherClick);

        return () => {
            map.current.off('click', handleRulerClick);
            map.current.off('click', handleWeatherClick);
            if (map.current.getLayer('RulerLayer')) map.current.removeLayer('RulerLayer');
            if (map.current.getSource('RulerSource')) map.current.removeSource('RulerSource');
            markersRef.current.forEach(m=>m.remove()); markersRef.current = [];
            if (rulerPopupRef.current) { rulerPopupRef.current.remove(); rulerPopupRef.current = null; }
            if (weatherMarkerRef.current) { weatherMarkerRef.current.remove(); weatherMarkerRef.current = null; }
            if (weatherPopupRef.current)  { weatherPopupRef.current.remove(); weatherPopupRef.current = null; }
        };
    }, [map, rulerActive, weatherActive]);

    return (
        <div className='widgets'>
            <div className='widgets-column'>
                <Tooltip title='Close all panels and reset the map to original state.'>
                    <div
                        onClick={() => {
                            setPanel({
                                name: 'Empty',
                                content: null
                            })
                            
                            map.current.flyTo({
                                center: [-97, 38],
                                zoom: 4,
                                pitch: 0
                            });

                            setWeatherActive(false);
                            setRulerActive(false);

                            setIs3D(false);

                            const layers = map.current.getStyle().layers.filter((layer) => layer.id.includes(`Layer-areas-focused`));

                            if (layers.length > 0) {
                                layers.forEach((layer) => map.current.removeLayer(layer.id))
                            }
                    
                            const sourceKeys = Object.keys(map.current.getStyle().sources);

                            const matchingSourceKeys = sourceKeys.filter(key =>
                                key.includes("Source-areas-focused")
                            );

                            matchingSourceKeys.forEach((source) => {
                                map.current.removeSource(source);
                            })

                            const existingPopups = document.getElementsByClassName('popup');
                            Array.from(existingPopups).forEach((popup) => popup.remove());
                        }}
                        className='widgets-widget'>
                        <img 
                            src={homeIcon} 
                            className='widgets-widget-img' 
                        />
                    </div>
                </Tooltip>
                <Tooltip
                    title='Customize the map by specifying areas to receive updates for or adding custom fire incidents directly to the application.'
                    arrow
                >
                    <div
                        onClick={() => {
                            if (panel.name === 'Create and Monitor') {
                                setPanel({
                                    name: 'Empty',
                                    content: null
                                })
                            } else {
                                setPanel({
                                    name: 'Create and Monitor',
                                    content: null
                                })
                            }
                        }}
                        className={panel.name === 'Create and Monitor' ? 'widgets-widget-active' : 'widgets-widget'}>
                        <img 
                            src={plusIcon} 
                            className='widgets-widget-img' 
                        />
                    </div>
                </Tooltip>
            </div>
            <div className='widgets-column'>
            <Tooltip
                title='Toggle the map view from 2D to 3D (vice-versa).'
                arrow
            >
            {map.current ?
                <div 
                    className='widgets-widget'
                    onClick={() => {
                        if (map.current) {
                            if (map.current.getPitch() !== 0) {
                                map.current.flyTo({
                                    pitch: 0
                                });
                                setIs3D(false);
                            } else {
                                map.current.flyTo({
                                    pitch: 65
                                });
                                
                                setIs3D(true);
                            }
                        }
                    }}
                >
                    <div style={{ fontWeight: 'bold', fontSize: '1rem', marginTop: '1px' }}>{is3D ? '2D' : '3D'}</div>
                </div>
                :
                <div 
                    className='widgets-widget'
                >
                    <div style={{ fontWeight: 'bold', fontSize: '1rem', marginTop: '1px' }}>3D</div>
                </div>}
            </Tooltip>
            <Tooltip
                title='Click on any two point on the map to calculate distance.'
                arrow
            >
                <div
                    onClick={() => {
                        if (weatherActive) {
                            setWeatherActive(false);
                        }
                        setRulerActive((prevState) => !prevState)
                    }}
                    className={rulerActive ? 'widgets-widget-active' : 'widgets-widget'}>
                    <img 
                        src={rulerIcon} 
                        className='widgets-widget-img' 
                    />
                </div>
            </Tooltip>
            </div>
            <div className='widgets-column'>
                <Tooltip
                    title='Zoom out to globe view.'
                    arrow    
                >
                    <div 
                        className={panel.name === 'Legend' ? 'widgets-widget-active' : 'widgets-widget'}
                        onClick={() => {
                            map.current.flyTo({
                                center: [-97, 38],
                                zoom: 1,
                                pitch: 0
                            });
                        }}
                        >
                        <img 
                            src={globeIcon} 
                            className='widgets-widget-img'
                        />
                    </div>
                </Tooltip>
                <Tooltip title='Click on any point on the map to view current weather conditions.'>
                    <div
                        className={`widgets-widget ${weatherActive ? 'widgets-widget-active' : ''}`}
                        onClick={() => {
                            if (rulerActive) {
                                setRulerActive(false);
                            }
                            setWeatherActive((prevState) => !prevState)

                        }}
                    >
                    <img src={weatherIcon} className='widgets-widget-img' alt='Weather'/>
                    </div>
                </Tooltip>
            </div>
        </div>
    )
}
